import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Space,
  Timeline,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type {
  PublicationListOut,
  SegmentErrorDetailOut,
} from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import {
  ProductFormatTag,
  ProductVariantTag,
  SegmentErrorReasonTag,
} from '@/components/progress/tags';
import { formatDateTime, formatSecondsClock } from '@/utils/format';
import { PublicationDrawerIcon } from '@/components/user_catalog/publications/drawer/PublicationDrawerIcon';

type Props = {
  record: SegmentErrorDetailOut;
  publication?: PublicationListOut;
};

export const SegmentErrorGeneralTab = ({ record, publication }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const basicItems: DescriptionsProps['items'] = [
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
    {
      key: 'segment_uuid',
      label: t('progress.segment_errors.fields.segment_uuid'),
      children: orDash(
        record.segment_uuid && (
          <Typography.Text code>{record.segment_uuid}</Typography.Text>
        )
      ),
    },
    {
      key: 'publisher_id',
      label: t('progress.segment_errors.fields.publisher_id'),
      children: orDash(record.publisher_id),
    },
    {
      key: 'account_id',
      label: t('progress.segment_errors.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t('progress.segment_errors.fields.profile_id'),
      children: orDash(
        record.profile_id != null && (
          <ProfileIconLink profileId={record.profile_id} />
        )
      ),
    },
    {
      key: 'device_id',
      label: t('progress.segment_errors.fields.device_id'),
      children: orDash(
        record.device_id && (
          <Typography.Text code>{record.device_id}</Typography.Text>
        )
      ),
    },
  ];

  const productItems: DescriptionsProps['items'] = [
    {
      key: 'publication_id',
      label: t('progress.segment_errors.fields.publication_id'),
      children: orDash(
        record.publication_id != null && (
          <PublicationInfoPopover
            publication={publication}
            publicationId={record.publication_id}
          />
        )
      ),
    },
    {
      key: 'product_id',
      label: t('progress.segment_errors.fields.product_id'),
      children: orDash(
        record.product_id != null && (
          <Space size={4}>
            {record.product_id}
            {record.publication_id != null && (
              <PublicationDrawerIcon publicationId={record.publication_id} />
            )}
          </Space>
        )
      ),
    },
    {
      key: 'product_version',
      label: t('progress.segment_errors.fields.product_version'),
      children: orDash(record.product_version),
    },
    {
      key: 'product_kind',
      label: t('progress.segment_errors.fields.product_kind'),
      children: orDash(
        record.product_kind && (
          <ProductFormatTag
            value={record.product_kind}
            labels="segment_errors"
          />
        )
      ),
    },
    {
      key: 'product_variant',
      label: t('progress.segment_errors.fields.product_variant'),
      children: orDash(
        record.product_variant && (
          <ProductVariantTag
            value={record.product_variant}
            labels="segment_errors"
          />
        )
      ),
    },
    {
      key: 'product_duration',
      label: t('progress.segment_errors.fields.product_duration'),
      children: orDash(
        record.product_duration != null && (
          <Tooltip title={`${record.product_duration} s`}>
            <span>
              {formatSecondsClock(record.product_duration, {
                alwaysHours: true,
              })}
            </span>
          </Tooltip>
        )
      ),
    },
    {
      key: 'product_demo_ratio',
      label: t('progress.segment_errors.fields.product_demo_ratio'),
      children: orDash(record.product_demo_ratio),
    },
    {
      key: 'resource_href',
      label: t('progress.segment_errors.fields.resource_href'),
      span: { xs: 1, sm: 2 },
      children: orDash(
        record.resource_href && (
          <Typography.Text
            code
            style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}
          >
            {record.resource_href}
          </Typography.Text>
        )
      ),
    },
  ];

  const progressionItems: DescriptionsProps['items'] = [
    {
      key: 'total_start_progression',
      label: t('progress.segment_errors.fields.total_start_progression'),
      children: orDash(record.total_start_progression?.toFixed(8)),
    },
    {
      key: 'total_end_progression',
      label: t('progress.segment_errors.fields.total_end_progression'),
      children: orDash(record.total_end_progression?.toFixed(8)),
    },
  ];

  // Only two stamps, but the order is the point: consumed on the device, then
  // synced to us — the rejection happened on arrival.
  const timelineSteps: { key: string; at: string | null }[] = [
    { key: 'consumed_at', at: record.consumed_at },
    { key: 'synced_at', at: record.synced_at },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={basicItems}
            />
          </Card>
          <Card title={t('progress.segment_errors.sections.product')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={productItems}
            />
          </Card>
          <Card title={t('progress.segment_errors.sections.progression')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2 }}
              items={progressionItems}
            />
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          <Card title={t('progress.segment_errors.sections.error')}>
            <Flex vertical gap={token.marginSM} align="flex-start">
              {orDash(
                record.reason && <SegmentErrorReasonTag value={record.reason} />
              )}
              <Typography.Paragraph
                style={{ margin: 0, whiteSpace: 'pre-wrap' }}
              >
                {orDash(record.detail)}
              </Typography.Paragraph>
            </Flex>
          </Card>
          <Card title={t('progress.segment_errors.sections.times')}>
            <Timeline
              items={timelineSteps.map(({ key, at }) => ({
                key,
                color: at ? 'blue' : 'gray',
                children: (
                  <Flex vertical gap={token.marginXXS}>
                    <Typography.Text type={at ? undefined : 'secondary'}>
                      {t(`progress.segment_errors.fields.${key}`)}
                    </Typography.Text>
                    <Typography.Text strong>
                      {orDash(formatDateTime(at))}
                    </Typography.Text>
                  </Flex>
                ),
              }))}
            />
          </Card>
        </Flex>
      </Col>
    </Row>
  );
};
